import React, { useState, useEffect, useRef } from 'react';
import { TrashIcon, PlusIcon } from "@heroicons/react/24/outline";
import VariableInput from '../Variables/VariableInput';

const QueryParamsEditor = ({ value, onChange, onUrlParamsChange }) => {
  const [params, setParams] = useState([{ key: '', value: '', enabled: true }]);
  // null (not `value`) so the very first mount always parses the incoming
  // value instead of assuming it's already in sync.
  const lastEmittedValue = useRef(null);

  useEffect(() => {
    // Skip syncing when `value` changed because this component just emitted
    // it via onChange — otherwise rows with a still-blank key (which can't
    // be represented in the serialized object) get wiped out mid-edit.
    if (value === lastEmittedValue.current) {
      return;
    }

    try {
      if (!value) {
        setParams([{ key: '', value: '', enabled: true }]);
        return;
      }

      const parsedParams = typeof value === 'string' ? JSON.parse(value) : value;
      const paramsArray = Object.entries(parsedParams).map(([key, value]) => ({
        key,
        value: value.toString(),
        enabled: true
      }));
      setParams(paramsArray.length ? paramsArray : [{ key: '', value: '', enabled: true }]);
    } catch (error) {
      console.error('Error parsing query parameters:', error);
      setParams([{ key: '', value: '', enabled: true }]);
    }
  }, [value]);

  const updateParams = (newParams) => {
    setParams(newParams);
    
    const enabledParams = newParams.filter(p => p.enabled && p.key.trim());
    
  
    const paramsObject = enabledParams.reduce((acc, p) => ({
      ...acc,
      [p.key]: p.value
    }), {});
    
    const queryString = enabledParams
      .map(p => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value)}`)
      .join('&');
    

    if (Object.keys(paramsObject).length === 0) {
      lastEmittedValue.current = "{}";
      onChange("{}");
      onUrlParamsChange('');
    } else {
      const serialized = JSON.stringify(paramsObject, null, 2);
      lastEmittedValue.current = serialized;
      onChange(serialized);
      onUrlParamsChange(queryString);
    }
  };

  const addParam = () => {
    updateParams([...params, { key: '', value: '', enabled: true }]);
  };

  const removeParam = (index) => {
    const newParams = params.filter((_, i) => i !== index);
    updateParams(newParams.length ? newParams : [{ key: '', value: '', enabled: true }]);
  };

  const updateParam = (index, field, value) => {
    const newParams = [...params];
    newParams[index] = { ...newParams[index], [field]: value };
    updateParams(newParams);
  };

  const toggleParam = (index) => {
    const newParams = [...params];
    newParams[index] = { ...newParams[index], enabled: !newParams[index].enabled };
    updateParams(newParams);
  };

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="flex-1 overflow-auto p-4">
        <div className="space-y-3">
          {params.map((param, index) => (
            <div
              key={index}
              className={`flex items-center gap-2 p-3 border rounded transition-colors ${
                param.enabled ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50'
              }`}
            >
              <input
                type="checkbox"
                checked={param.enabled}
                onChange={() => toggleParam(index)}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 cursor-pointer"
              />
              <div className="flex-1 flex gap-2">
                <div className="flex-1">
                  <input
                    type="text"
                    value={param.key}
                    onChange={(e) => updateParam(index, 'key', e.target.value)}
                    placeholder="Parameter name"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500 font-mono"
                  />
                </div>
                <div className="flex-1">
                  <VariableInput
                    value={param.value}
                    onChange={(value) => updateParam(index, 'value', value)}
                    placeholder="Value"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500 font-mono"
                    showAutocomplete={true}
                  />
                </div>
              </div>
              <button
                onClick={() => removeParam(index)}
                className={`p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded ${
                  params.length === 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                }`}
                disabled={params.length === 1}
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          ))}

          <button
            onClick={addParam}
            className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded w-full justify-center"
          >
            <PlusIcon className="h-4 w-4" />
            Add Parameter
          </button>
        </div>
      </div>
    </div>
  );
};

export default QueryParamsEditor;
